import { useRef, type CSSProperties, type ReactElement } from "react";
import { motion } from "framer-motion";
import { LayoutGrid, GanttChartSquare } from "lucide-react";
import { color, radius, space, motion as motionTokens, usePrefersReducedMotion } from "@tokens/index";
import { motionStyle } from "@primitives/motionStyle";
import { SlidingPill } from "@primitives/SlidingPill";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";

// Canonical set of workflow detail views. "board" is the task-forward
// default; "timeline" is the read-only audit view.
export type WorkflowViewMode = "board" | "timeline";

interface ViewOption {
    readonly id: WorkflowViewMode;
    readonly label: string;
    readonly icon: typeof LayoutGrid;
    /** Reserved slots render disabled until their view ships. */
    readonly disabled?: boolean;
}

const VIEW_OPTIONS: readonly ViewOption[] = [
    { id: "board", label: "Board", icon: LayoutGrid },
    { id: "timeline", label: "Timeline", icon: GanttChartSquare },
];

export interface WorkflowViewSwitcherProps {
    readonly value: WorkflowViewMode;
    readonly onChange: (value: WorkflowViewMode) => void;
    /**
     * Force icon-only rendering. Without it the control shows icon and
     * label on `expanded` and wider, and icon only below.
     */
    readonly compact?: boolean;
    /**
     * A segment other than the current one was hovered or focused. The
     * orchestrator prefetches that view's data so the switch lands warm.
     */
    readonly onIntent?: (value: WorkflowViewMode) => void;
}

const SEGMENT_HEIGHT = 32;

const rootStyle: CSSProperties = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    padding: 2,
    borderRadius: radius.pill,
    background: color.bg.surface2,
    border: `1px solid ${color.border.subtle}`,
    flexShrink: 0,
};

function segmentStyle(active: boolean, disabled: boolean, iconOnly: boolean): CSSProperties {
    return {
        position: "relative",
        zIndex: 1,
        appearance: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: space[1],
        height: SEGMENT_HEIGHT,
        minWidth: iconOnly ? 36 : undefined,
        paddingInline: iconOnly ? space[2] : space[3],
        border: "none",
        borderRadius: radius.pill,
        background: "transparent",
        color: active ? color.accent.onPrimary : disabled ? color.fg.subtle : color.fg.muted,
        opacity: disabled ? 0.56 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: `color ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
    };
}

/**
 * Segmented control selecting the workflow detail view. Presentational:
 * the orchestrator owns the value and mirrors it into the URL.
 */
export function WorkflowViewSwitcher({
    value,
    onChange,
    compact = false,
    onIntent,
}: WorkflowViewSwitcherProps): ReactElement {
    const { isAtLeast } = useViewport();
    const reduced = usePrefersReducedMotion();
    const containerRef = useRef<HTMLDivElement>(null);
    const iconOnly = compact || !isAtLeast("expanded");
    const activeIndex = VIEW_OPTIONS.findIndex((option) => option.id === value);

    return (
        <div ref={containerRef} role="radiogroup" aria-label="Workflow view" style={rootStyle}>
            <SlidingPill
                containerRef={containerRef}
                activeIndex={activeIndex}
                itemSelector="[data-view-option]"
                style={{ borderRadius: radius.pill, background: color.accent.primary }}
            />
            {VIEW_OPTIONS.map((option) => {
                const Icon = option.icon;
                const active = value === option.id;
                const disabled = option.disabled === true;
                const intent = (): void => {
                    if (!disabled && !active) onIntent?.(option.id);
                };
                return (
                    <motion.button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        aria-disabled={disabled}
                        aria-label={iconOnly ? option.label : undefined}
                        disabled={disabled}
                        title={disabled ? `${option.label} — coming soon` : option.label}
                        data-view-option={option.id}
                        data-hit-area=""
                        onPointerEnter={intent}
                        onFocus={intent}
                        onClick={() => {
                            if (!disabled && !active) {
                                onChange(option.id);
                            }
                        }}
                        {...(disabled || reduced
                            ? {}
                            : {
                                  whileTap: { scale: motionTokens.press.scale },
                                  transition: { duration: motionTokens.press.duration },
                              })}
                        style={motionStyle(segmentStyle(active, disabled, iconOnly))}
                    >
                        <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
                        {!iconOnly && (
                            <Text
                                role="caps"
                                size="xs"
                                weight={active ? "semibold" : "medium"}
                                style={{ color: "inherit", lineHeight: 1.2 }}
                            >
                                {option.label}
                            </Text>
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
}
